import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { getSspParams } from "../../../services/etcServices";

import useStore from "../../../hooks/useStore";
import { useDeviceType } from "../../../hooks/useDeviceType/Index";
import useSimplePopup from "../../../hooks/useSimplePopup";
import { RefreshIcon } from "../../../hooks/useStoreHeader";
import useShowActivity from "../../../hooks/useShowActivity";

import StickyHeader from "../../../components/Layout/StickyHeader";
import BottomGroup from "../../../components/Button/Group";
import CommonButton from "../../../components/Button";

type Props = {};

export default function CustomService(props: Props) {
  const EMPTY_HISTORY = "emptyHistory";

  const receivedResponse = useRef<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [iframeUrl, setIframeUrl] = React.useState("");
  const [buttonList, setButtonList] = React.useState<any[]>([]);
  const deviceType = useDeviceType();
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [storeCall] = useStore({ open });
  const [param, setParam] = React.useState<any>({});
  const [showActivityCall] = useShowActivity({ open });

  const handleMessage = (e: MessageEvent) => {
    if (e.origin === location.state?.targetOrigin) {
      if (!receivedResponse.current) {
        receivedResponse.current = true;
      }

      if (e.data === EMPTY_HISTORY) {
        navigate("/view");
      }
    }
  };

  const sendRefreshPostMessage = () => {
    if (!location.state?.refreshMessage || !location.state?.targetOrigin) {
      return;
    }

    const iframe = document.getElementById("myIframe") as HTMLIFrameElement;
    if (!iframe?.contentWindow) {
      return;
    }

    iframe.contentWindow?.postMessage(
      location.state.refreshMessage,
      location.state.targetOrigin
    );
  };

  const sendBackPostMessage = () => {
    if (!location.state?.backMessage || !location.state?.targetOrigin) {
      return;
    }

    const iframe = document.getElementById("myIframe") as HTMLIFrameElement;
    if (!iframe?.contentWindow) {
      navigate("/view");
      return;
    }

    iframe.contentWindow?.postMessage(
      location.state.backMessage,
      location.state.targetOrigin
    );

    if (!receivedResponse.current) {
      setTimeout(() => {
        if (!receivedResponse.current) {
          navigate("/view");
        }
      }, 1000);
    }
  };

  const loadSspParams = async () => {
    try {
      const result = await getSspParams();
      if (result.status === HttpStatusCode.Ok) {
        setParam({
          ...result.data,
          targetUrl: location.state.actionJson?.[0]?.link,
        });
      } else {
        open("SSP 파라미터 조회에 실패했습니다.");
      }
    } catch (e: any) {
      open("SSP 파라미터 조회에 실패했습니다.");
    }
  };

  const jsonToQueryString = (json: any) => {
    return Object.keys(json)
      .map((key) => {
        return encodeURIComponent(key) + "=" + encodeURIComponent(json[key]);
      })
      .join("&");
  };

  useEffect(() => {
    if (location.state.serviceItemType === "SSP") {
      loadSspParams();
    }
    if (location.state.action === "INFO") {
      setButtonList(
        location.state.actionJson.filter((item: any) => item.btn) ?? []
      );
    } else if (
      location.state.action === "LINK" &&
      location.state.actionJson.length > 0 &&
      location.state.actionJson[0].target === "inner"
    ) {
      setIframeUrl(location.state.actionJson[0].link);
    } else if (location.state?.iframeUrl) {
      setIframeUrl(location.state.iframeUrl);
    }

    if (location.state?.backMessage) {
      window.addEventListener("message", handleMessage);

      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, []);

  return (
    <div>
      <StickyHeader
        title={location.state.serviceName}
        onBackClick={() => {
          if (location.state?.backMessage) {
            sendBackPostMessage();
          } else {
            navigate("/view");
          }
        }}
        className="px-5"
        rightContent={
          location.state?.refreshMessage && (
            <div onClick={() => sendRefreshPostMessage()}>
              <RefreshIcon />
            </div>
          )
        }
      />
      <div className=" max-w-[360px] break-all hidden" data-testid="ssp-json">
        {JSON.stringify(param)}
      </div>
      <div
        className=" max-w-[360px] break-all hidden"
        data-testid="ssp-query-string"
      >
        {jsonToQueryString(param)}
      </div>

      {iframeUrl && (
        <iframe
          id="myIframe"
          src={iframeUrl}
          className="w-full h-[calc(100vh-60px)]"
          title="inner-frame"
          data-testid="my-iframe"
        />
      )}
      {!iframeUrl && location.state.action === "INFO" && (
        <img
          src={
            location.state.actionInfoFileInfo?.items.find(
              (item: any) => item.scale === "full"
            )?.url
          }
          className={clsx([
            "w-full object-contain",
            {
              "pb-[50px]": location.state.useBtn,
            },
          ])}
        />
      )}

      {buttonList.length > 0 && (
        <BottomGroup className="flex">
          {buttonList.map((item: any, index) => (
            <CommonButton
              key={index}
              variant={buttonList.length - 1 === index ? "primary" : "grey"}
              className={clsx("rounded-none w-full h-bottom-button", {
                "w-[100px]":
                  buttonList.length > 1 && index < buttonList.length - 1,
                "w-full":
                  buttonList.length <= 1 || index >= buttonList.length - 1,
              })}
              onClick={() => {
                if (item.target === "outer") {
                  if (location.state.serviceItemType === "SSP") {
                    window.open("/callback/ssp?" + jsonToQueryString(param));
                  } else if (item.action === "phone") {
                    window.location.href = `tel:${item.link}`;
                  } else if (item.action === "web") {
                    window.open(item.link);
                  } else if (item.action === "app") {
                    if (deviceType === "Android") {
                      if (item.storeAndroid) {
                        storeCall(item.schemeAndroid, item.storeAndroid);
                      } else {
                        open("안드로이드 앱 정보가 없습니다.");
                      }
                    } else if (deviceType === "iOS") {
                      if (item.storeIos) {
                        storeCall(item.schemeIos, item.storeIos);
                      } else {
                        open("iOS 앱 정보가 없습니다.");
                      }
                    } else {
                      open("데스크탑에서는 앱 연동은 미지원 상태입니다.");
                    }
                  } else if (item.action === "activity") {
                    showActivityCall(
                      location.state.serviceName,
                      item?.link ?? ""
                    );
                  } else {
                    console.log("unknown item action", item.action);
                  }
                } else if (item.target === "inner") {
                  setButtonList([]);
                  setIframeUrl(item.link);
                }
              }}
              data-testid="bottom-button"
            >
              {item.btn}
            </CommonButton>
          ))}
        </BottomGroup>
      )}
      {simplePopupComponent}
    </div>
  );
}
